import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations/translations';

const Hero = () => {
  const { language } = useLanguage();
  const t = translations[language];
  return (
    <section className="relative min-h-[85vh] flex items-center justify-center bg-gradient-to-b from-gray-950 via-gray-900 to-black text-white overflow-hidden pt-20 md:pt-24">
      {/* CCTV Grid Pattern Background - Subtle */}
      <div className="absolute inset-0 security-grid opacity-3"></div>
      
      {/* Animated Scan Line Effect */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute w-full h-0.5 bg-gradient-to-r from-transparent via-green-500/20 to-transparent animate-scan-line"></div>
      </div>

      {/* Camera Feed Grid Overlay - Animated but subtle */}
      <div className="absolute inset-0 opacity-4">
        <div className="grid grid-cols-4 gap-3 h-full p-6">
          {[...Array(8)].map((_, i) => (
            <div 
              key={i} 
              className="bg-gray-900/40 border border-green-500/15 rounded-lg relative overflow-hidden backdrop-blur-sm"
              style={{ 
                animationDelay: `${i * 0.3}s`,
                animation: `flicker 4s infinite`
              }}
            >
              {/* REC Indicator - Subtle */}
              <div className="absolute top-2 left-2 flex items-center space-x-1 z-10 opacity-40">
                <div className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse"></div>
                <span className="text-[10px] text-green-400/60 font-mono">REC</span>
              </div>
              {/* Animated Camera Feed Lines */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-0 left-0 w-full h-px bg-green-500/30 animate-scan-line"></div>
                <div className="absolute top-1/3 left-0 w-full h-px bg-green-500/20 animate-scan-line" style={{ animationDelay: '1.5s' }}></div>
                <div className="absolute top-2/3 left-0 w-full h-px bg-green-500/20 animate-scan-line" style={{ animationDelay: '3s' }}></div>
              </div>
              {/* Camera Number - Very subtle */}
              <div className="absolute bottom-1.5 right-1.5 text-[7px] text-green-400/25 font-mono">CAM{i+1}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Rotating Camera Effect */}
      <div className="absolute top-20 right-20 w-16 h-16 border-2 border-green-500/30 rounded-full animate-radar-sweep pointer-events-none">
        <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-1 h-4 bg-green-500 rounded-full"></div>
      </div>
      <div className="absolute bottom-20 left-20 w-12 h-12 border-2 border-green-500/30 rounded-full animate-radar-sweep pointer-events-none" style={{ animationDelay: '1.5s' }}>
        <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-1 h-3 bg-green-500 rounded-full"></div>
      </div>

      {/* Corner Brackets (Security System Style) */}
      <div className="absolute top-0 left-0 w-32 h-32 border-t-4 border-l-4 border-green-500 opacity-50"></div>
      <div className="absolute top-0 right-0 w-32 h-32 border-t-4 border-r-4 border-green-500 opacity-50"></div>
      <div className="absolute bottom-0 left-0 w-32 h-32 border-b-4 border-l-4 border-green-500 opacity-50"></div>
      <div className="absolute bottom-0 right-0 w-32 h-32 border-b-4 border-r-4 border-green-500 opacity-50"></div>

      {/* Content */}
      <div className="relative z-20 container mx-auto px-4 sm:px-6 lg:px-8 text-center pt-8 md:pt-12">
        <div className="max-w-4xl mx-auto">
          {/* CCTV Badge */}
          <div className="inline-block mb-4 px-5 py-2 bg-black/90 backdrop-blur-md rounded-full border-2 border-green-500/60 shadow-xl shadow-green-500/30">
            <div className="flex items-center space-x-2 justify-center">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse-glow flex-shrink-0"></div>
              <span className="text-xs font-bold text-green-300 font-mono tracking-wide drop-shadow-[0_0_8px_rgba(34,197,94,0.8)]">{t.heroBadge}</span>
            </div>
          </div>
          
          {/* Main Heading - Compact */}
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold mb-4 leading-tight font-mono text-white drop-shadow-[0_0_20px_rgba(0,0,0,0.8)]">
            <span className="block text-white">{t.heroTitle1}</span>
            <span className="block bg-gradient-to-r from-green-400 via-cyan-400 to-green-500 bg-clip-text text-transparent animate-pulse-glow drop-shadow-[0_0_15px_rgba(34,197,94,0.5)]">
              {t.heroTitle2}
            </span>
          </h1>
          
          <p className="text-lg md:text-xl mb-3 text-gray-200 font-light font-mono drop-shadow-[0_0_10px_rgba(0,0,0,0.7)]">
            {t.heroSubtitle}
          </p>
          <p className="text-base md:text-lg mb-6 text-gray-100 max-w-2xl mx-auto font-medium drop-shadow-[0_0_8px_rgba(0,0,0,0.6)]">
            {t.heroDescription}
          </p>
          
          {/* CTA Buttons - Compact */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-10">
            <a 
              href="#services" 
              className="group relative px-8 py-4 bg-gradient-to-r from-green-600 to-cyan-600 text-white rounded-xl font-bold text-base overflow-hidden transition-all duration-300 transform hover:scale-105 shadow-xl shadow-green-500/40 border-2 border-green-500/50"
            >
              <span className="relative z-10 flex items-center">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                {t.heroButton1}
              </span>
            </a>
            <a 
              href="#contact" 
              className="px-8 py-4 bg-transparent border-2 border-green-500 text-green-400 rounded-xl font-bold text-base hover:bg-green-500 hover:text-black transition-all duration-300 transform hover:scale-105 backdrop-blur-sm font-mono"
            >
              {t.heroButton2}
            </a>
          </div>

          {/* Stats - Compact Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
            {[
              { number: '500+', label: t.heroStat1, icon: '🔒' },
              { number: '1000+', label: t.heroStat2, icon: '📹' },
              { number: '15+', label: t.heroStat3, icon: '⭐' },
              { number: '24/7', label: t.heroStat4, icon: '👁️' }
            ].map((stat, index) => (
              <div key={index} className="bg-black/60 backdrop-blur-sm rounded-xl p-4 border-2 border-green-500/30 transform hover:scale-105 transition-all duration-300 hover:border-green-500/60 hover:shadow-lg shadow-green-500/20">
                <div className="text-2xl mb-1">{stat.icon}</div>
                <div className="text-2xl md:text-3xl font-bold mb-1 text-green-400 font-mono">{stat.number}</div>
                <div className={`text-xs text-gray-300 ${language === 'hi' ? '' : 'uppercase'} tracking-wider`}>{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll Indicator - Compact */}
      <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="flex flex-col items-center">
          <span className="text-xs mb-1 text-green-400 font-mono">{t.heroScroll}</span>
          <svg className="w-5 h-5 text-green-500" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
            <path d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
          </svg>
        </div>
      </div>
    </section>
  );
};

export default Hero;
